import { Link, useLocation } from "@tanstack/react-router";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "../../components/ui/sidebar";
import { useCurrentUser, useHasPermission } from "../../features/auth/session";
import { useResearchSummary } from "../../features/research/research-queries";
import { type NavItem, visibleNav } from "./nav-items";
import { NavUser } from "./nav-user";

/** "/research/123" is inside Research; the dashboard is only "/". */
function isActive(pathname: string, item: NavItem): boolean {
  return item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function AppSidebar() {
  const { permissions } = useCurrentUser();
  const canApprove = useHasPermission("research.approve");
  const summary = useResearchSummary(canApprove);
  const waiting = summary.data?.waitingApproval ?? 0;
  const pathname = useLocation({ select: (location) => location.pathname });
  const { setOpenMobile } = useSidebar();

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" aria-label="Pokifolio home" onClick={() => setOpenMobile(false)}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sm font-bold text-sidebar-primary-foreground">
                  P
                </div>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-semibold">Pokifolio</span>
                  <span className="truncate text-xs text-muted-foreground">Card research</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label="Main">
          {visibleNav(permissions).map((group) => (
            <SidebarGroup key={group.title}>
              <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
              <SidebarMenu>
                {group.items.map((item) => {
                  const count = item.to === "/approvals" ? waiting : 0;
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={isActive(pathname, item)} tooltip={item.title}>
                        <Link
                          to={item.to}
                          onClick={() => setOpenMobile(false)}
                          {...(count > 0 ? { "aria-label": `${item.title} (${String(count)} waiting)` } : {})}
                        >
                          <item.icon aria-hidden="true" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                      {count > 0 && (
                        <SidebarMenuBadge
                          aria-hidden="true"
                          className="rounded-full bg-amber-100 text-amber-900 peer-hover/menu-button:text-amber-900 dark:bg-amber-500/20 dark:text-amber-200"
                        >
                          {count}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
